<template>
  <Dialog :open="props.showProfile" @update:open="onClose">
    <DialogContent class="sm:max-w-[480px]">
      <DialogHeader>
        <DialogTitle>Profile</DialogTitle>
        <DialogDescription>
          <div class="flex flex-col gap-2">
            <span>Manage your profile info below.</span>
            <span v-if="errorMsg" class="text-error-700 font-medium text-xs">
              {{ errorMsg }}
            </span>
            <span v-if="successMsg" class="text-green-500 font-medium text-xs">
              {{ successMsg }}
            </span>
          </div>
        </DialogDescription>
      </DialogHeader>
      <div class="flex flex-col gap-4 py-4" v-if="!isLoading">
        <div class="flex flex-col gap-2">
          <LabelComponent>First Name</LabelComponent>
          <InputComponent type="text" v-model="form.firstName" />
          <span v-if="errors.firstName !== ''" class="text-error-700 font-medium text-xs">{{ errors.firstName }}</span>
        </div>
        <div class="flex flex-col gap-2">
          <LabelComponent>Last Name</LabelComponent>
          <InputComponent type="text" v-model="form.lastName" />
          <span v-if="errors.lastName !== ''" class="text-error-700 font-medium text-xs">{{ errors.lastName }}</span>
        </div>
        <div class="flex flex-col gap-2">
          <LabelComponent>Mobile Number</LabelComponent>
          <InputComponent type="text" v-model="form.mobileNumber" />
          <span v-if="errors.mobileNumber !== ''" class="text-error-700 font-medium text-xs">
            {{ errors.mobileNumber }}
          </span>
        </div>
        <div class="flex flex-col gap-2">
          <LabelComponent>Email</LabelComponent>
          <InputComponent type="text" v-model="form.email" />
          <span v-if="errors.email !== ''" class="text-error-700 font-medium text-xs">{{ errors.email }}</span>
          <span
            v-if="!user.verifiedEmail"
            class="text-brand-700 font-semibold text-xs hover:cursor-pointer"
            @click="resendEmailVerification"
          >
            Resend email verification
          </span>
        </div>
        <div class="my-4 h-[1px] w-full bg-grey-light-200"></div>
        <div class="flex flex-col gap-2">
          <LabelComponent>New Password</LabelComponent>
          <InputComponent type="password" v-model="form.newPassword" />
          <span v-if="errors.newPassword !== ''" class="text-error-700 font-medium text-xs">
            {{ errors.newPassword }}
          </span>
        </div>
        <div class="flex flex-col gap-2">
          <LabelComponent>Confirm Password</LabelComponent>
          <InputComponent type="password" v-model="form.confirmPassword" />
          <span v-if="errors.confirmPassword !== ''" class="text-error-700 font-medium text-xs">
            {{ errors.confirmPassword }}
          </span>
        </div>
      </div>

      <LayupSpinner v-if="isLoading" />

      <DialogFooter>
        <Button type="button" variant="outline" class="w-full" @click="onClose"> Cancel </Button>
        <Button type="button" @click.prevent="saveProfile">Save</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { NetworkError } from '@/errors';
import { sendEmailVerification } from '@/services/auth';
import { useUserStore } from '@/stores/users';
import { isValidEmail, isValidMobile, isValidPassword, parseMobile } from '@/utils/form.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { storeToRefs } from 'pinia';
import { reactive, ref, watch } from 'vue';
import type { UserServiceApiUserServiceUpdateRequest } from '@layup/api-sdk';
import { updateUser } from '../../services/user';
import LayupSpinner from '../common/LayupSpinner.vue';
import InputComponent from '../ui/input/InputComponent.vue';
import LabelComponent from '../ui/label/LabelComponent.vue';

const emit = defineEmits(['update:toggleProfile']);

const props = defineProps<{
  showProfile: boolean;
}>();

const userStore = useUserStore();
const { user } = storeToRefs(userStore);

const form = reactive({
  firstName: '',
  lastName: '',
  mobileNumber: '',
  email: '',
  newPassword: '',
  confirmPassword: '',
});

const errors = reactive({
  firstName: '',
  lastName: '',
  mobileNumber: '',
  email: '',
  newPassword: '',
  confirmPassword: '',
});

const isLoading = ref(false);
const errorMsg = ref('');
const successMsg = ref('');

watch(
  () => form.mobileNumber,
  (newValue) => {
    if (newValue) {
      form.mobileNumber = parseMobile(newValue);
    }
  },
);

watch([() => props.showProfile, () => user], ([showProfile, currentUser]) => {
  if (showProfile || currentUser) {
    currentUser.value.verifiedEmail;
    const nameParts = currentUser.value.name?.split(' ') || [];
    form.firstName = nameParts[0] || '';
    form.lastName = nameParts[1] || '';
    form.mobileNumber = currentUser.value.cellNumber || '';
    form.email = currentUser.value.email || '';
  }
});

const resendEmailVerification = async () => {
  errorMsg.value = '';
  isLoading.value = true;
  const data = {
    name: userStore.user.name,
    email: userStore.user.email,
    _id: userStore.user._id,
  };

  await sendEmailVerification(data)
    .then(() => (successMsg.value = 'Email verification link was sent successfully.'))
    .catch(() => (errorMsg.value = 'Sending email verification was unsuccessfully. Please try again later.'));

  isLoading.value = false;
};

const saveProfile = async () => {
  try {
    isLoading.value = true;
    errorMsg.value = '';
    successMsg.value = '';
    if (formValid()) {
      const userData: UserServiceApiUserServiceUpdateRequest = {
        id: user.value._id || '',
        modelUserRequest: {
          name: `${form.firstName} ${form.lastName}`,
          cellNumber: form.mobileNumber,
          email: form.email,
        },
      };

      if (form.newPassword && form.confirmPassword) {
        userData.modelUserRequest.password = form.newPassword;
      }

      const userResponse = await updateUser(userData);
      userStore.user = userResponse;
      form.newPassword = '';
      form.confirmPassword = '';
      addMetric(`update_profile`, { status: 'success', channel: 'merchant' });
      onClose();
    }
  } catch (error: any) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric(`update_profile`, { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric(`update_profile`, { status: 'error', channel: 'merchant' });
      if (error?.response?.data) {
        errorMsg.value = error.response.data;
      } else {
        errorMsg.value = 'An unexpected error occurred. Please try again.';
      }
    }
  } finally {
    isLoading.value = false;
  }
};

const formValid = (): boolean => {
  let isValid = true;

  if (form.firstName === '') {
    errors.firstName = 'Please enter your first name.';
    isValid = false;
  } else if (!/^[A-Za-z][A-Za-z'-]{1,}$/.test(form.firstName)) {
    errors.firstName = 'Please enter a valid first name.';
    isValid = false;
  } else {
    errors.firstName = '';
  }

  if (form.lastName === '') {
    errors.lastName = 'Please enter your last name.';
    isValid = false;
  } else if (!/^[A-Za-z][A-Za-z'-]{1,}$/.test(form.lastName)) {
    errors.lastName = 'Please enter a valid last name.';
    isValid = false;
  } else {
    errors.lastName = '';
  }

  if (!form.mobileNumber.trim()) {
    errors.mobileNumber = 'Please enter your mobile number.';
    isValid = false;
  } else if (!isValidMobile(form.mobileNumber)) {
    errors.mobileNumber = 'Please enter a valid mobile number.';
    isValid = false;
  } else {
    errors.mobileNumber = '';
  }

  if (!form.email.trim()) {
    errors.email = 'Please enter your email address.';
    isValid = false;
  } else if (!isValidEmail(form.email)) {
    errors.email = 'Please enter a valid email address.';
    isValid = false;
  } else {
    errors.email = '';
  }

  if (form.newPassword !== '' && form.confirmPassword === '') {
    isValid = false;
    errors.confirmPassword = 'Please confirm your password.';
  } else if (form.newPassword === '' && form.confirmPassword !== '') {
    isValid = false;
    errors.newPassword = 'Please enter your password.';
  } else if (form.newPassword !== '' && form.confirmPassword !== '') {
    const passwordValidation = isValidPassword(form.newPassword, form.confirmPassword);
    if (!passwordValidation.isValid) {
      errors.newPassword = passwordValidation.errorMessage;
      isValid = false;
    } else {
      errors.newPassword = '';
      errors.confirmPassword = '';
    }
  }

  return isValid;
};

const onClose = () => {
  emit('update:toggleProfile', false);
  form.firstName = '';
  form.lastName = '';
  form.mobileNumber = '';
  form.email = '';
  form.newPassword = '';
  form.confirmPassword = '';

  errorMsg.value = '';
  errors.firstName = '';
  errors.lastName = '';
  errors.mobileNumber = '';
  errors.email = '';
  errors.newPassword = '';
  errors.confirmPassword = '';
};
</script>
